import { describeInstallments } from 'features/common/helpers/installments';
import { AUTHORIZATION_CHANNELS, AuthorizationChannel } from '../constants/authorizationChannels';
import { MerchantNumberRule } from '../constants/merchantNumbers';
import { BaseCarrierRule } from './types';

// ─── Las reglas de la base en la forma de la tabla fija ──────────────────────
// El modal de autorización y "Códigos de comercio y cuotas" usan las reglas de
// `GET /rules` con los mismos componentes y helpers que la tabla fija del Power
// (`MERCHANT_NUMBER_RULES`): acá se valida lo que llega y se pasa cada regla a
// esa forma. Funciones puras.

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const KNOWN_CHANNELS: readonly string[] = AUTHORIZATION_CHANNELS;

/**
 * `2026-12-31` (o con hora, `2026-12-31T…`) → `31/12/2026`, como la tabla fija (así la lee `isMerchantRuleExpired`).
 * Sin fecha, "sin vencimiento".
 */
export function merchantValidity(validUntil: string | null): string {
	if (!validUntil) return 'sin vencimiento';
	const day = ISO_DAY.exec(validUntil.slice(0, 10));
	return day ? `${day[3]}/${day[2]}/${day[1]}` : validUntil;
}

/**
 * Una regla de la base como una fila de la tabla fija. El comercio va con las marcas que la tabla traduce: no acepta
 * (o canal `no_cards`) → "No acepta tarjetas"; sin comercio y por GDS → "Solo por GDS". Las cuotas, de la lista. Lleva
 * su canal (uno que no se conoce, "sin definir"): el modal autoriza por ahí (`resolveEffectiveAuthorizationChannel`).
 */
export function toMerchantRule(rule: BaseCarrierRule): MerchantNumberRule {
	let merchantNumber = rule.merchantNumber || '—';
	if (!rule.merchantNumber && (rule.acceptance === 'gds_only' || rule.channel === 'gds')) merchantNumber = 'GDS_ONLY';
	if (rule.acceptance === 'not_accepted' || rule.channel === 'no_cards') merchantNumber = 'NOT_ACCEPTED';
	const channel: AuthorizationChannel = KNOWN_CHANNELS.includes(rule.channel) ? rule.channel : 'unknown';
	return {
		carrierCode: rule.carrierCode,
		carrierName: rule.carrierName,
		cardLabel: rule.cardLabel,
		cardStamps: [rule.cardBrand],
		merchantNumber,
		installmentsLabel: describeInstallments(rule.installments),
		validUntil: merchantValidity(rule.validUntil),
		notes: rule.notes ?? undefined,
		channel,
	};
}

const TEXTS = ['carrierCode', 'carrierName', 'cardBrand', 'cardLabel', 'acceptance', 'channel'];
const TEXTS_OR_NULL = ['merchantNumber', 'validUntil', 'notes'];

/** ¿Tiene la forma que se dibuja? Un campo de otro tipo (una fecha numérica, notas como objeto) la rompería. */
function isRuleShape(rule: unknown): rule is BaseCarrierRule {
	if (!rule || typeof rule !== 'object') return false;
	const field = rule as Record<string, unknown>;
	return (
		typeof field.id === 'number' &&
		typeof field.active === 'boolean' &&
		TEXTS.every((key) => typeof field[key] === 'string') &&
		TEXTS_OR_NULL.every((key) => field[key] === null || typeof field[key] === 'string') &&
		Array.isArray(field.installments) &&
		field.installments.every(Number.isInteger)
	);
}

/**
 * Las reglas vigentes (`active`) de lo que respondió `/rules`, en el orden de carga. `null` si no se le puede creer:
 * alguna regla de otra forma, o hay más (`pagination.total`) que las que vinieron.
 */
export function activeRulesFrom(data: unknown): BaseCarrierRule[] | null {
	const response = data as { rules?: unknown; pagination?: { total?: unknown } } | null | undefined;
	const rules = response?.rules;
	const total = response?.pagination?.total;
	if (!Array.isArray(rules) || !rules.every(isRuleShape)) return null;
	if (typeof total === 'number' && total > rules.length) return null;
	return rules.filter((rule) => rule.active).sort((a, b) => a.id - b.id);
}

/**
 * Las reglas vigentes como las lee el modal de autorización (`authorizationGuide`): una fila por regla, con su canal,
 * y en una sola, con sus sellos, las de una aerolínea que no difieren en nada más (la semilla abre por sello lo que en
 * la tabla fija es una fila: Copa "Mastercard / Oca", Lufthansa). Con la semilla da la tabla fija.
 */
export function merchantRulesOf(rules: readonly BaseCarrierRule[]): MerchantNumberRule[] {
	const rows = new Map<string, MerchantNumberRule>();
	rules.map(toMerchantRule).forEach((row) => {
		const { carrierCode, carrierName, cardLabel, merchantNumber, installmentsLabel, validUntil, notes, channel } = row;
		const key = JSON.stringify([
			carrierCode,
			carrierName,
			cardLabel,
			merchantNumber,
			installmentsLabel,
			validUntil,
			notes,
			channel,
		]);
		const same = rows.get(key);
		// `set` sobre una clave que ya está no la mueve: la fila queda donde apareció primero.
		rows.set(key, same ? { ...same, cardStamps: Array.from(new Set([...same.cardStamps, ...row.cardStamps])) } : row);
	});
	return Array.from(rows.values());
}
